import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import SVGInline from 'react-svg-inline';

// Icons
import previousArrow from 'assets/icon-left-arrow.svg';
import nextArrow from 'assets/icon-right-arrow.svg';

// Styles
import styles from './timeframeScrubber.scss';

class TimeframeScrubber extends Component {
  /**
   * Given an array, slice off the beginning so only a certain number is showing
   * and return an object containing the new array and its boundaries.
   * @param  {array}  options.timeframes  Array to slice
   * @param  {number} options.numVisible  How many items to show
   * @return {object}                     A configuration object with the
   *                                        visible timeframes and the keys with
   *                                        the beginning and end indexes that
   *                                        are visible.
   */
  static sliceTimeframes({ timeframes, numVisible = 6 }) {
    const numberToHide = Math.floor(timeframes.length - numVisible);
    return {
      timeframes: timeframes.slice(numberToHide, timeframes.length),
      keys: {
        begin: numberToHide,
        end: 0,
      },
    };
  }

  /**
   * Given an interval and the boundaries, will return a boolean answering the
   * question if the visible array can shift by the interval given.
   * @param  {number} intervalToAdd The amount of visible keys to shift.
   * @param  {number} options.begin Zero-based index of the first visible key.
   * @param  {number} options.end   Zero-based negative index of the last visible
   *                                key in the array.
   * @return {bool}                 Whether or not next boundary exists.
   */
  static canNavigateDirection(intervalToAdd, { begin, end }) {
    const isLookingForward = 0 < intervalToAdd;

    if (isLookingForward) {
      // The end parameter should be a negative number. It will only be positive
      // if we are already at the end of the array.
      return 0 > end && 0 >= (intervalToAdd + end);
    }

    return 0 <= (intervalToAdd + begin);
  }

  /**
   * Given a direction, update the array of visible years to shift that number
   * over.
   * @param  {number} numberToShift Number of intervals to shift.
   * @param  {number} options.begin Zero-based index of the first visible key.
   * @param  {number} options.end   Zero-based negative index of the last visible
   *                                key in the array.
   * @param  {array}  allTimeframes Array of all timeframes to search for the
   *                                new visible timeframes
   * @return {object}               Object to update component state with new
   *                                       visibleTimeframes and keyBoundaries.
   */
  static shiftVisibleIntervals(numberToShift, { begin, end }, allTimeframes) {
    if (
      !TimeframeScrubber.canNavigateDirection(numberToShift, { begin, end })
    ) {
      return {};
    }

    const newKeyBoundaries = {
      begin: numberToShift + begin,
      end: numberToShift + end,
    };

    const newTimeframes = allTimeframes.slice(
      newKeyBoundaries.begin,
      0 === newKeyBoundaries.end ? allTimeframes.length : newKeyBoundaries.end,
    );

    return {
      visibleTimeframes: newTimeframes,
      keyBoundaries: newKeyBoundaries,
    };
  }

  /**
   * Create component.
   * @param  {object} props React props.
   * @return {void}
   */
  constructor(props) {
    super(props);

    const {
      timeframes = this.props.timeframes,
      keys = { begin: 0, end: timeframes.length },
    } = TimeframeScrubber.sliceTimeframes(props);

    this.state = {
      visibleTimeframes: timeframes,
      keyBoundaries: keys,
    };

    this.handleIntervalControl = this.handleIntervalControl.bind(this);
    this.intervalControl = this.intervalControl.bind(this);
  }

  /**
   * Update state when interval controls are pressed
   * @param  {number} numberToShift Number of intervals to move.
   * @return {void}
   */
  handleIntervalControl(numberToShift) {
    this.setState((prevState, props) =>
      TimeframeScrubber.shiftVisibleIntervals(
        numberToShift,
        prevState.keyBoundaries,
        props.timeframes
      )
    );
  }

  /**
   * Markup for the interval control
   * @param  {string} label            Screen reader text for the control.
   * @param  {object} svg              SVG asset
   * @param  {number} intervalToChange Direction to shift the timeline
   * @return {object}                  Component jsx.
   */
  intervalControl(
    label,
    svg,
    intervalToChange
  ) {
    const enabled = TimeframeScrubber
      .canNavigateDirection(intervalToChange, this.state.keyBoundaries);

    return (
      <button
        className={styles.intervalControl}
        onClick={() => this.handleIntervalControl(intervalToChange)}
        disabled={!enabled}
      >
        <SVGInline
          svg={svg}
          className={styles.icon}
          cleanup
        />
        <span className="screen-reader-text">
          {label}
        </span>
      </button>
    );
  }

  /**
   * Render component.
   * @return {object} Component jsx.
   */
  render() {
    const { timeframes, activeTimeframe, onClick } = this.props;
    const { visibleTimeframes } = this.state;
    return (
      <ul className={styles.timeframeScrubber}>
        {timeframes.length > visibleTimeframes.length &&
          <li className={styles.intervalItem}>
            {this.intervalControl(
              'Show previous years on the timeline',
              previousArrow,
              -1
            )}
          </li>
        }
        <li className={styles.scrubberItem}>
          <ul className={styles.scrubber}>
            {visibleTimeframes.map(({ label, value }) => (
              <li
                key={label}
                className={styles.item}
              >
                <button
                  onClick={() => onClick(value)}
                  className={classNames({
                    [styles.timeframeControl]: true,
                    [styles.activeTimeframeControl]: value === activeTimeframe,
                  })}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>
        </li>
        {timeframes.length > visibleTimeframes.length &&
          <li className={styles.intervalItem}>
            {this.intervalControl(
              'Show later years on the timeline',
              nextArrow,
              1
            )}
          </li>
        }
      </ul>
    );
  }
}

TimeframeScrubber.propTypes = {
  activeTimeframe: PropTypes.number.isRequired,
  timeframes: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  onClick: PropTypes.func.isRequired,
};

export default TimeframeScrubber;



// WEBPACK FOOTER //
// ./app/components/timeframeScrubber/index.js